"use client";
import { useState } from "react";
import { usePathname } from "next/navigation";
import makeApiCall from "@packages/lib/server-actions/rest-api";
import API_URL from "@packages/lib/server-actions/api-urls";
import {
  clickstreamForClicks,
  gaForCtaClicks,
  isWhatuni,
  oldGAForCtaClicks,
} from "@packages/lib/utlils/commonFunction";
import { ClickstreamInterface } from "@packages/lib/utlils/clickstream";
import { ClickstreamConstants, GAConstants } from "@packages/shared-components/services/constants";
import { GA4DataLayer } from "@packages/lib/utlils/helper-function";
import ButtonLoader from "./ButtonLoader";
import { buildBookingUrl } from "@packages/lib/utlils/revenue-buttons/button-logic-util";
import { getCustomDomain } from "@packages/lib/utlils/common-function-server";
import { useIsLoggedInUser } from "@packages/hooks/LoggedInUser";
import ExitIntentForm from "@packages/shared-components/exit-intent-form/exit-intent-form";

const BookOpenDay = ({
  enquiryProps,
  commonGAAttrs,
  btnSize = "w-full",
  btnName,
  commonOldGAAttrs,
  eventType,
  hasVeppleSection,
  bookingUrl,
  institutionTextKey,
  institutionId
}: any) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showExitIntent, setShowExitIntent] = useState<boolean>(false);
  const [isLoggedInUser] = useIsLoggedInUser();
  const pathname = usePathname();
  const getButtonText = () => isWhatuni() ? (btnName || "Book open day") : "Book event";

  // Show exit-intent popup for guest users once per session (external URLs only)
  // Whatuni only — exit-intent flow is not enabled for PGS.
  const triggerExitIntent = () => {
    if (
      isWhatuni() &&
      !isLoggedInUser &&
      !sessionStorage.getItem("exitIntentShown")
    ) {
      sessionStorage.setItem("exitIntentShown", "true");
      setShowExitIntent(true);
    }
  };

  const createCSData = (): ClickstreamInterface => ({
    CTATitle: getButtonText(),
    actionType: ClickstreamConstants.clickAction,
    functionalityName: enquiryProps?.sponsoredListingFlag === "Y" ? "Sponsor listing" : "Openday",
  });

  const getSectionData = (id:any): string | null => {
    const element = document.getElementById("book-openday-btn");
    const sectionName = element?.getAttribute(id) || null;
    element?.removeAttribute(id);
    return sectionName;
  };

  const getClickText = (buttonText: any) => {
    if (buttonText) return buttonText;
    if (eventType === "VIRTUAL EVENT"|| eventType==="Virtual Event") return "book_virtual_event";
    if (eventType === "VIRTUAL TOUR"|| eventType==="Virtual Tour") return "take_virtual_tour";
    return "book_open_day";
  };

  const buildGAAttrs = (sectionName:any,buttonText:any) => ({
    ...commonGAAttrs,
    page_section: sectionName || commonGAAttrs.page_section,
    study_mode: enquiryProps?.studyMode || "na",
    click_text: getClickText(buttonText),
    institution_name: institutionTextKey ?? "na",
    institution_id: institutionId ?? "na",
    //...(sessionStorage.getItem("veppleProviders") && { data_label5: "vepple_driven" }),
  });
  const buildOldGAAttrs = (sectionName:any,buttonText:any) => ({
    ...commonOldGAAttrs,
    data_label: sectionName || commonGAAttrs.page_section,
    study_mode: enquiryProps?.studyMode || "na",
    cta_name: buttonText || "Book open day",
    college_name: institutionTextKey ?? "na",
    college_id: institutionId ?? "na",
    ...(sessionStorage.getItem("veppleProviders") && { data_label5: "vepple_driven" }),
  });

  const createPayload = () => ({
    suborderItemId: enquiryProps?.subOrderItemId,
    orderItemId: enquiryProps?.orderItemId,
    collegeId: enquiryProps?.collegeId,
    courseId: enquiryProps?.courseId,
    affiliateId: process.env.AFFILATE_ID,
    qualCode: enquiryProps?.qualCode,
  });

  const processBookingResponse = (response: any) => {
    const isInternal = response?.onsiteBookingForm?.toLowerCase() === "y";
    return {
      url: isInternal
        ? buildBookingUrl(response, enquiryProps)
        : bookingUrl || response?.bookingUrl,
      eventType: isInternal ? "BookEventInternalLinkClicked" : "BookEventExternalLinkClicked",
      isInternal,
    };
  };

  const handleBookOpenDay = async () => {
      if (eventType?.toLowerCase() === 'virtual tour') {
      const localGAAttrs = buildGAAttrs(null, null);

      let ctaUrl = "";
      if (hasVeppleSection) {
        const targetPath = `/open-days/${institutionTextKey}/${institutionId}/`;
        const currentPath = pathname?.endsWith('/') ? pathname : pathname + '/';

        if (currentPath === targetPath) {
          // Same page — scroll to vepple section with offset
          ctaUrl = targetPath + '#vepple';
          const veppleElement = document.getElementById('vepple-section') || document.getElementById('vepple');
          if (veppleElement) {
            const y = veppleElement.getBoundingClientRect().top + window.scrollY - 100;
            window.scrollTo({ top: y, behavior: 'smooth' });
          }
        } else {
          // Different page — show loader and navigate
          setIsLoading(true);
          ctaUrl = targetPath + '#vepple';
          window.location.href = ctaUrl;
        }
      } else if (bookingUrl) {
        ctaUrl = bookingUrl;
        window.open(bookingUrl, "_blank");
        triggerExitIntent();
      }

      GA4DataLayer({
        event: GAConstants.track_event,
        event_name: "vepple_virtual_tour_request",
        click_text: "take_virtual_tour",
        ...localGAAttrs,
      });

      return;
    }

    setIsLoading(true);
    const sectionName = getSectionData("data-section-name");
    const buttonText = getSectionData("data-button-text");
    const localGAAttrs = buildGAAttrs(sectionName,buttonText);
    const localOldGAAttrs = buildOldGAAttrs(sectionName,buttonText);
    const csData = createCSData();

    try {
      const response = await makeApiCall({
        url: `${process.env.NEXT_PUBLIC_DOMSERVICE_API_DOMAIN}/${API_URL?.getEnquiryDetails}`,
        method: "POST",
        body: createPayload(),
        headers: { "x-api-key": process.env.NEXT_PUBLIC_DOMSERVICE_X_API_KEY || "" },
      });

      const { url, eventType, isInternal } = processBookingResponse(response);
      Object.assign(csData, {
        eventType,
        eventId: enquiryProps?.eventId || response?.eventId || "",
        externalUrl: bookingUrl ? bookingUrl : response?.bookingUrl || "",
      });
      enquiryProps.event = isInternal ? (isWhatuni() ? "book_event_request" : "open_days_internal") : (isWhatuni() ?  "book_event_external" : "open_days_external"),
      enquiryProps.url = isInternal ? getCustomDomain() + url : url;
      // Revenue value for the book_open_day_* revenue_kpi event (DLG-11778).
      // Coerce to a Number (the API can return it as a string) and only
      // attribute per-click revenue on a CPE booking (cpeFlag === "Y"); a
      // tenancy (non-CPE) click has no per-click revenue, so send 0 rather than
      // a raw/large price. Internal booking uses the event price, external the
      // per-click open day price.
      const rawRevenue = isInternal
        ? response?.opendayEventPrice
        : response?.opendayPrice;
      enquiryProps.revenueValue =
        response?.cpeFlag === "Y" ? Number(rawRevenue) || 0 : 0;
      gaForCtaClicks(
        enquiryProps,
        isInternal ? "book_open_day_internal" : "book_open_day_external",
        localGAAttrs,
        response?.cpeFlag,
        ""
      );
       oldGAForCtaClicks(
        enquiryProps,
        isWhatuni() ? "Book open day" : "Book event",
        enquiryProps.url,
        enquiryProps.event,
        localOldGAAttrs,
        response?.cpeFlag,
        ""
      );
      
      await clickstreamForClicks(enquiryProps, csData, commonGAAttrs);
      window.open(url, isInternal ? "_self" : "_blank");
      // Exit-intent only for external bookings
      if (!isInternal) {
        triggerExitIntent();
      }
    } catch (error) {
      console.error("Error fetching enquiry data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <button
        type="button"
        className={`btn btn-green w-full flex items-center justify-center text-center ${btnSize}`}
        onClick={handleBookOpenDay}
        id="book-openday-btn"
      >
        <ButtonLoader isLoading={isLoading}>
          {getButtonText()}
        </ButtonLoader>
      </button>

      {showExitIntent && (
        <ExitIntentForm
          sourceClickText="book_open_day"
          onClose={() => setShowExitIntent(false)}
          onSubmit={() => {}}
        />
      )}
    </>
  );
};

export default BookOpenDay;
